import React, { useState, useEffect, useRef } from 'react';
import { banksService } from '../../../services/api';
// Styles provided by RestaurantOnboarding.css

const BANKS_FALLBACK = [
  { id: 1,  name: 'B. Pichincha',         cod_bank: '10', color: '#00A651', abbr: 'BP' },
  { id: 2,  name: 'B. Produbanco',        cod_bank: '36', color: '#003087', abbr: 'PR' },
  { id: 3,  name: 'B. Guayaquil',         cod_bank: '17', color: '#E31837', abbr: 'BG' },
  { id: 4,  name: 'B. Pacífico',          cod_bank: '22', color: '#0064B1', abbr: 'PA' },
  { id: 5,  name: 'B. Internacional',     cod_bank: '32', color: '#004B87', abbr: 'BI' },
  { id: 6,  name: 'B. Bolivariano',       cod_bank: '07', color: '#1B3A6B', abbr: 'BB' },
  { id: 7,  name: 'B. del Austro',        cod_bank: '03', color: '#C8922A', abbr: 'AU' },
  { id: 8,  name: 'B. Solidario',         cod_bank: '39', color: '#6B2D8B', abbr: 'SO' },
  { id: 9,  name: 'BanEcuador',           cod_bank: '47', color: '#009944', abbr: 'BE' },
  { id: 10, name: 'Mutualista Pichincha', cod_bank: '62', color: '#E8A020', abbr: 'MP' },
];

/* Adds color/abbr if API doesn't return them */
const enrich = (bank) => {
  const fallback = BANKS_FALLBACK.find((b) => b.id === bank.id) || {};
  const abbr = bank.abbr || fallback.abbr
    || bank.name.split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase();
  const color = bank.color || fallback.color || '#FF8700';
  return { ...bank, abbr, color };
};

/* Colored avatar — shows logo image when available, falls back to initials */
const BankAvatar = ({ bank, size = 'md' }) =>
  bank.icon ? (
    <span className={`ro-bank-avatar ro-bank-avatar--${size} ro-bank-avatar--img`} aria-hidden>
      <img src={bank.icon} alt={bank.name} className="ro-bank-avatar-img" />
    </span>
  ) : (
    <span
      className={`ro-bank-avatar ro-bank-avatar--${size}`}
      style={{ background: bank.color }}
      aria-hidden
    >
      {bank.abbr}
    </span>
  );

/* Custom bank dropdown */
const BankDropdown = ({ banks, value, onChange, error }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const selected = banks.find((b) => b.id === value);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div className="ro-bank-sel" ref={ref}>
      <button
        type="button"
        className={`ro-bank-trigger${error ? ' ro-bank-trigger--err' : ''}${open ? ' ro-bank-trigger--open' : ''}`}
        onClick={() => setOpen((o) => !o)}
      >
        {selected ? (
          <>
            <BankAvatar bank={selected} size="sm" />
            <span className="ro-bank-trigger-name">{selected.name}</span>
          </>
        ) : (
          <span className="ro-bank-trigger-placeholder">Selecciona tu banco…</span>
        )}
        <span className={`ro-bank-chevron${open ? ' ro-bank-chevron--up' : ''}`}>▾</span>
      </button>

      {open && (
        <ul className="ro-bank-dropdown">
          {banks.map((bank) => (
            <li key={bank.id}>
              <button
                type="button"
                className={`ro-bank-option${value === bank.id ? ' ro-bank-option--sel' : ''}`}
                onMouseDown={() => { onChange(bank.id); setOpen(false); }}
              >
                <BankAvatar bank={bank} />
                <span className="ro-bank-option-name">{bank.name}</span>
                {value === bank.id && <span className="ro-bank-option-check">✓</span>}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

/* ─────────────── Main step ─────────────── */
const CI_META = {
  CI:        { max: 10, label: 'Cédula',    holder: '10 dígitos',   numeric: true  },
  RUC:       { max: 13, label: 'RUC',       holder: '13 dígitos',   numeric: true  },
  PASAPORTE: { max: 15, label: 'Pasaporte', holder: 'Ej. A1234567', numeric: false },
};

const StepRestaurantBank = ({ formData, onUpdate, errors = {}, onClearError }) => {
  const [banks,   setBanks]   = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    banksService.getAll(formData.city_id)
      .then((res) => {
        const list = res?.data ?? res ?? BANKS_FALLBACK;
        setBanks((Array.isArray(list) ? list : BANKS_FALLBACK).map(enrich));
      })
      .catch(() => setBanks(BANKS_FALLBACK))
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.city_id]);

  /* Auto-sync nombre del titular desde el representante legal */
  useEffect(() => {
    const full = [formData.representative_name, formData.representative_last_name]
      .filter(Boolean).join(' ');
    if (full) onUpdate({ bank_owner_name: full });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.representative_name, formData.representative_last_name]);

  /* Auto-sync cédula from billing when type is CI */
  useEffect(() => {
    if ((formData.bank_ci_type || 'CI') === 'CI' && formData.representative_dni) {
      onUpdate({ bank_ci_number: formData.representative_dni });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.bank_ci_type, formData.representative_dni]);

  const handleChange = (field, value) => {
    onUpdate({ [field]: value });
    if (onClearError) onClearError(field);
  };

  const handleAccountNumber = (raw) =>
    handleChange('bank_account_number', raw.replace(/\D/g, '').slice(0, 22));

  const ciType = formData.bank_ci_type || 'CI';
  const ciMeta = CI_META[ciType];
  const ciIsAutoFilled = ciType === 'CI';

  const handleCi = (raw) => {
    const value = ciMeta.numeric
      ? raw.replace(/\D/g, '').slice(0, ciMeta.max)
      : raw.replace(/[^a-zA-Z0-9]/g, '').toUpperCase().slice(0, ciMeta.max);
    handleChange('bank_ci_number', value);
  };

  const handleCiType = (type) => {
    const updates = { bank_ci_type: type, bank_ci_number: '' };
    /* Si vuelve a CI, rellenar automáticamente con la cédula del representante */
    if (type === 'CI' && formData.representative_dni) {
      updates.bank_ci_number = formData.representative_dni;
    }
    onUpdate(updates);
    if (onClearError) { onClearError('bank_ci_number'); onClearError('bank_ci_type'); }
  };

  return (
    <div className="ro-body ro-basic">

      <div className="ro-step-hd ro-step-hd--sm">
        <span className="ro-eyebrow ro-eyebrow--accent">Paso 9 · Banco</span>
        <h2 className="ro-title">Información bancaria</h2>
      </div>

      {/* ── Nota cuenta a nombre del representante ── */}
      <div className="ro-bank-note">
        <span className="ro-bank-note-ico" aria-hidden>💡</span>
        <p>
          La cuenta bancaria debe estar <strong>a nombre de la persona que presenta el RUC</strong>.
          Validaremos esta información antes de activar tu cuenta.
        </p>
      </div>

      {/* ── Bank selector ── */}
      <div className="ro-field">
        <label className="ro-lbl">Banco<span className="ro-req">*</span></label>
        {loading ? (
          <div className="ro-city-loading"><span className="ro-spin" /></div>
        ) : (
          <BankDropdown
            banks={banks}
            value={formData.bank_id}
            onChange={(id) => handleChange('bank_id', id)}
            error={errors.bank_id}
          />
        )}
        {errors.bank_id && <span className="ro-err-msg">{errors.bank_id}</span>}
      </div>

      {/* ── Account type + Number ── */}
      <div className="ro-bank-row">
        <div className="ro-field">
          <label className="ro-lbl">Tipo de cuenta<span className="ro-req">*</span></label>
          <div className="ro-seg">
            {['AHORRO', 'CORRIENTE'].map((t) => (
              <button
                key={t}
                type="button"
                className={`ro-seg-btn${formData.bank_account_type === t ? ' ro-seg-btn--sel' : ''}`}
                onClick={() => handleChange('bank_account_type', t)}
              >
                {t === 'AHORRO' ? 'Ahorros' : 'Corriente'}
              </button>
            ))}
          </div>
          {errors.bank_account_type && <span className="ro-err-msg">{errors.bank_account_type}</span>}
        </div>

        <div className="ro-field">
          <label className="ro-lbl">Número de cuenta<span className="ro-req">*</span></label>
          <div className={`ro-inputwrap${errors.bank_account_number ? ' ro-inputwrap--err' : ''}`}>
            <span className="ro-inputico" aria-hidden>💳</span>
            <input
              type="text"
              inputMode="numeric"
              className="ro-input--clean"
              placeholder="Ej. 2214611339"
              value={formData.bank_account_number || ''}
              onChange={(e) => handleAccountNumber(e.target.value)}
            />
          </div>
          {errors.bank_account_number && <span className="ro-err-msg">{errors.bank_account_number}</span>}
        </div>
      </div>

      {/* ── Owner name (auto-filled from representative) ── */}
      <div className="ro-field">
        <label className="ro-lbl">Nombre del titular<span className="ro-req">*</span></label>
        <div className={`ro-inputwrap ro-inputwrap--locked${errors.bank_owner_name ? ' ro-inputwrap--err' : ''}`}>
          <span className="ro-inputico" aria-hidden>👤</span>
          <input
            type="text"
            className="ro-input--clean"
            value={formData.bank_owner_name || ''}
            readOnly
          />
          <span className="ro-inputsuffix ro-bank-autofill-badge">autocompletado</span>
        </div>
        <span className="ro-bank-autofill-hint">
          Usamos el nombre del representante legal que ingresaste en facturación.
        </span>
        {errors.bank_owner_name && <span className="ro-err-msg">{errors.bank_owner_name}</span>}
      </div>

      {/* ── Document type + number ── */}
      <div className="ro-field">
        <label className="ro-lbl">Documento del titular<span className="ro-req">*</span></label>
        <div className="ro-seg ro-seg--mb">
          {Object.entries(CI_META).map(([key, meta]) => (
            <button
              key={key}
              type="button"
              className={`ro-seg-btn${ciType === key ? ' ro-seg-btn--sel' : ''}`}
              onClick={() => handleCiType(key)}
            >
              {meta.label}
            </button>
          ))}
        </div>
        <div className={`ro-inputwrap${errors.bank_ci_number ? ' ro-inputwrap--err' : ''}${ciIsAutoFilled ? ' ro-inputwrap--locked' : ''}`}>
          <span className="ro-inputico" aria-hidden>🪪</span>
          <input
            type="text"
            inputMode={ciMeta.numeric ? 'numeric' : 'text'}
            className="ro-input--clean"
            placeholder={ciMeta.holder}
            value={formData.bank_ci_number || ''}
            onChange={ciIsAutoFilled ? undefined : (e) => handleCi(e.target.value)}
            readOnly={ciIsAutoFilled}
            maxLength={ciMeta.max}
          />
          {ciIsAutoFilled ? (
            <span className="ro-inputsuffix ro-bank-autofill-badge">autocompletado</span>
          ) : (
            <span className="ro-inputsuffix ro-phone-count">
              {(formData.bank_ci_number || '').length}/{ciMeta.max}
            </span>
          )}
        </div>
        {ciIsAutoFilled && (
          <span className="ro-bank-autofill-hint">
            Usamos la cédula que ingresaste en el paso de facturación.
          </span>
        )}
        {errors.bank_ci_number && <span className="ro-err-msg">{errors.bank_ci_number}</span>}
      </div>

    </div>
  );
};

export default StepRestaurantBank;
